/**
 * Two drives down one road, drawn side by side.
 *
 * A day that goes out along a road and comes back along the same road drew
 * both traversals on the same coordinates, so the second one landed exactly on
 * the first. What reached the screen was one line - and one line is what a
 * road driven ONCE looks like, so the map was quietly telling the reader the
 * wrong thing about the day. The direction arrows made it worse rather than
 * better: two sets of them, pointing opposite ways, alternating along a single
 * stroke.
 *
 * So a stretch the day drives BOTH ways is shifted to the right of its own
 * direction of travel. Two opposite traversals each keep right, which puts
 * them on opposite sides of the centreline without either of them having to
 * know about the other - the same rule traffic already follows, and the reason
 * the result reads as two carriageways rather than as a drawing mistake.
 *
 * A stretch driven once is not moved. Nothing is merged: each traversal stays
 * the leg it belongs to, in the order it is driven, and keeps its own arrows.
 *
 * OFFSET IN METRES, not in pixels. A pixel offset stays the same width on
 * screen at every zoom, which sounds better and is the thing to avoid here: at
 * a city-wide zoom a six-pixel shift is half a kilometre on the ground, and
 * the route stops lying on the road it describes. Metres cannot do that. The
 * cost is that the two lines converge as you zoom out, which is honest - at
 * that scale there is no room to show them apart, and the alternative is a
 * route floating in a field.
 */

/** A point as Leaflet orders them: [latitude, longitude]. */
export type RoutePoint = [number, number];

/**
 * How far off the centreline each direction is drawn.
 *
 * About half a carriageway, so the pair straddles the road rather than
 * standing beside it. Bigger reads as two different roads; smaller does not
 * separate at the zoom somebody is at when they care.
 */
export const RouteOffsetMeters = 6;

/** Close enough to be the same corner: about a metre on the ground, which is
 *  finer than any road geometry and coarser than float noise. */
const KeyDecimals = 5;

const MetersPerDegreeLat = 111_320;

/** Cosine never taken below this, so a segment near the pole cannot divide by
 *  something indistinguishable from zero. */
const MinCos = 1e-6;

/** Names a corner, to the tolerance two traversals of one road agree to. */
function pointKey(point: RoutePoint): string {
  return `${point[0].toFixed(KeyDecimals)},${point[1].toFixed(KeyDecimals)}`;
}

/** Names a segment the same whichever way it is driven. */
export function segmentKey(a: RoutePoint, b: RoutePoint): string {
  const from = pointKey(a);
  const to = pointKey(b);
  return from < to ? `${from}|${to}` : `${to}|${from}`;
}

/**
 * The stretches this day drives in BOTH directions.
 *
 * Decided over every leg at once, because the return trip is a DIFFERENT leg -
 * a leg on its own has no idea it is retracing one that came before it.
 *
 * Driving the same stretch twice the same way is not this: two overlapping
 * lines pointing the same way say "twice", which is true, and pulling them
 * apart would invent a second road. Only a disagreement about direction needs
 * two lines to be read at all.
 */
export function doubledSegments(
  legs: readonly (readonly RoutePoint[])[],
): Set<string> {
  const directions = new Map<string, Set<string>>();

  for (const leg of legs) {
    for (let i = 0; i < leg.length - 1; i++) {
      const key = segmentKey(leg[i], leg[i + 1]);
      let seen = directions.get(key);
      if (!seen) {
        seen = new Set<string>();
        directions.set(key, seen);
      }
      seen.add(`${pointKey(leg[i])}>${pointKey(leg[i + 1])}`);
    }
  }

  const doubled = new Set<string>();
  for (const [key, seen] of directions) {
    if (seen.size > 1) doubled.add(key);
  }
  return doubled;
}

/**
 * One leg, shifted to the right of travel wherever the road is driven both
 * ways, and left where it is not.
 *
 * A corner takes the MEAN of the segments meeting at it, so a stretch that
 * starts or stops being doubled eases across rather than jumping sideways -
 * and a there-and-back that turns round mid-leg pinches to the centreline at
 * the turn, which is what a U-turn looks like from above.
 *
 * Degrees of longitude are divided by cos(latitude): a degree east is shorter
 * than a degree north everywhere but the equator, and without it the two lines
 * would sit six metres apart running north and nine running east.
 */
export function offsetAlongRight(
  points: readonly RoutePoint[],
  doubled: ReadonlySet<string>,
  meters: number = RouteOffsetMeters,
): RoutePoint[] {
  if (points.length < 2) return points.map(point => [point[0], point[1]]);

  const shifts: RoutePoint[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    shifts.push(doubled.has(segmentKey(points[i], points[i + 1]))
      ? rightOf(points[i], points[i + 1], meters)
      : [0, 0]);
  }

  return points.map((point, i) => {
    const before = i > 0 ? shifts[i - 1] : null;
    const after = i < shifts.length ? shifts[i] : null;
    const parts = [before, after].filter((shift): shift is RoutePoint => !!shift);
    const lat = parts.reduce((sum, shift) => sum + shift[0], 0) / parts.length;
    const lng = parts.reduce((sum, shift) => sum + shift[1], 0) / parts.length;
    return [point[0] + lat, point[1] + lng];
  });
}

/**
 * How far to move, in degrees, to stand `meters` to the right of the drive
 * from `a` to `b`.
 *
 * Right of travel is the heading turned a quarter turn clockwise: heading
 * north, right is east.
 */
function rightOf(a: RoutePoint, b: RoutePoint, meters: number): RoutePoint {
  const toRad = Math.PI / 180;
  const cos = Math.max(MinCos, Math.abs(Math.cos(((a[0] + b[0]) / 2) * toRad)));

  const north = (b[0] - a[0]) * MetersPerDegreeLat;
  const east = (b[1] - a[1]) * MetersPerDegreeLat * cos;
  const length = Math.hypot(north, east);
  // A segment with no length has no direction to be right of.
  if (length === 0) return [0, 0];

  const rightEast = north / length;
  const rightNorth = -east / length;
  return [
    (rightNorth * meters) / MetersPerDegreeLat,
    (rightEast * meters) / (MetersPerDegreeLat * cos),
  ];
}
